import React, { useEffect, useState } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Fade,
  Zoom,
  Grow,
} from '@mui/material'
import {
  TrendingUp,
  TrendingDown,
  People,
  ShoppingCart,
  AttachMoney,
  Assessment,
  Refresh,
  Circle,
  CheckCircle,
  AutoAwesome,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { useDashboardUpdates, useWebSocket } from '../../hooks/useWebSocket'
import { dashboardAPI } from '../../services/api'
import { updateDashboardMetrics } from '../../store/slices/realtimeSlice'
import { GradientMetricCard } from '../../components/Dashboard/GradientMetricCard'
import { RecentActivityList } from '../../components/Dashboard/RecentActivityList'
import { OrderStatusChart } from '../../components/Dashboard/OrderStatusChart'
import { EmployeeUtilizationChart } from '../../components/Dashboard/EmployeeUtilizationChart'
import { DashboardMobileView } from './DashboardMobileView'

export const DashboardPage: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dispatch = useAppDispatch()
  const { user, token } = useAppSelector((state) => state.auth)
  const { dashboardMetrics, recentActivities, isConnected } = useAppSelector((state) => state.realtime)
  const { metrics, lastUpdate } = useDashboardUpdates()
  const [isLoaded, setIsLoaded] = useState(false)

  // Initialize WebSocket connection
  useWebSocket({
    token: token || '',
    autoConnect: true,
    channels: ['dashboard', 'orders', 'employees'],
  })

  // Load initial dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const metricsData = await dashboardAPI.getMetrics()
        dispatch(updateDashboardMetrics(metricsData))
        setIsLoaded(true)
      } catch (error) {
        console.error('Failed to load dashboard data:', error)
        setIsLoaded(true)
      }
    }

    loadDashboardData()
  }, [dispatch])

  // Update metrics when WebSocket data changes
  useEffect(() => {
    if (metrics && Object.keys(metrics).length > 0) {
      dispatch(updateDashboardMetrics(metrics))
    }
  }, [metrics, dispatch])

  const handleRefresh = async () => {
    try {
      const metricsData = await dashboardAPI.getMetrics()
      dispatch(updateDashboardMetrics(metricsData))
    } catch (error) {
      console.error('Failed to refresh dashboard data:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success'
      case 'in_progress':
        return 'warning'
      case 'pending':
        return 'info'
      case 'cancelled':
        return 'error'
      default:
        return 'default'
    }
  }

  // Use mobile view on mobile devices
  if (isMobile) {
    return <DashboardMobileView />
  }

  // Desktop view
  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, rgba(103, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)',
    }}>
      {/* Header */}
      <Fade in={isLoaded} timeout={800}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
          p: 3,
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
        }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <AutoAwesome sx={{ color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Dashboard
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
              Welcome back, {user?.full_name}! Here's what's happening today.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              icon={<Circle sx={{ fontSize: 10, animation: isConnected ? 'pulse 2s infinite' : 'none', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } }} />}
              label={isConnected ? 'Live' : 'Offline'}
              size="small"
              sx={{
                bgcolor: isConnected ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                color: isConnected ? 'success.main' : 'error.main',
                fontWeight: 600,
                border: '1px solid',
                borderColor: isConnected ? 'success.main' : 'error.main',
              }}
            />
            <Tooltip title="Refresh data" arrow>
              <IconButton
                onClick={handleRefresh}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(103, 126, 234, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px) rotate(180deg)',
                    boxShadow: '0 6px 20px rgba(103, 126, 234, 0.4)',
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>

      {/* Gradient Metrics Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        {[
          { title: "Total Orders", value: dashboardMetrics.totalOrders || 0, icon: <ShoppingCart />, gradient: "blue", trend: 5.2, subtitle: "vs last month", delay: 200 },
          { title: "Active Orders", value: dashboardMetrics.activeOrders || 0, icon: <Assessment />, gradient: "orange", subtitle: "In progress", delay: 300 },
          { title: "Total Employees", value: dashboardMetrics.totalEmployees || 0, icon: <People />, gradient: "pink", trend: 2.1, subtitle: "Active staff", delay: 400 },
          { title: "Monthly Revenue", value: `Rs. ${dashboardMetrics.monthlyRevenue?.toLocaleString() || 0}`, icon: <AttachMoney />, gradient: "green", trend: 8.7, subtitle: "vs last month", delay: 500 }
        ].map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Zoom in={isLoaded} timeout={card.delay}>
              <Box>
                <GradientMetricCard
                  title={card.title}
                  value={card.value}
                  icon={card.icon}
                  gradient={card.gradient as any}
                  trend={card.trend}
                  subtitle={card.subtitle}
                />
              </Box>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Performance Metrics */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Grow in={isLoaded} timeout={600}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              height: '100%',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{
                    p: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <Assessment sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'onSurface', fontWeight: 600 }}>
                    Production Efficiency
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {dashboardMetrics.productionEfficiency || 0}%
                  </Typography>
                  <TrendingUp sx={{ ml: 1, color: 'success.main', fontSize: 32 }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dashboardMetrics.productionEfficiency || 0}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(103, 126, 234, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: 5,
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grow>
        </Grid>
        <Grid item xs={12} md={4}>
          <Grow in={isLoaded} timeout={700}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{
                    p: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'onSurface', fontWeight: 600 }}>
                    Order Completion Rate
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {dashboardMetrics.orderCompletionRate || 0}%
                  </Typography>
                  <TrendingUp sx={{ ml: 1, color: 'success.main', fontSize: 32 }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dashboardMetrics.orderCompletionRate || 0}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #4caf50 0%, #388e3c 100%)',
                      borderRadius: 5,
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grow>
        </Grid>
        <Grid item xs={12} md={4}>
          <Grow in={isLoaded} timeout={800}>
            <Card sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              },
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Box sx={{
                    p: 1,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <People sx={{ color: 'white', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" sx={{ color: 'onSurface', fontWeight: 600 }}>
                    Employee Utilization
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h3" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {dashboardMetrics.employeeUtilization || 0}%
                  </Typography>
                  <TrendingDown sx={{ ml: 1, color: 'error.main', fontSize: 32 }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dashboardMetrics.employeeUtilization || 0}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #2196f3 0%, #1976d2 100%)',
                      borderRadius: 5,
                    }
                  }}
                />
              </CardContent>
            </Card>
          </Grow>
        </Grid>
      </Grid>

      {/* Charts and Activity */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Fade in={isLoaded} timeout={900}>
            <Paper sx={{
              p: 4,
              height: 400,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <ShoppingCart sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ color: 'onSurface', fontWeight: 600 }}>
                  Order Status Overview
                </Typography>
              </Box>
              <OrderStatusChart />
            </Paper>
          </Fade>
        </Grid>
        <Grid item xs={12} md={4}>
          <Fade in={isLoaded} timeout={1000}>
            <Paper sx={{
              p: 4,
              height: 400,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Assessment sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ color: 'onSurface', fontWeight: 600 }}>
                  Recent Activity
                </Typography>
              </Box>
              <RecentActivityList activities={recentActivities} />
            </Paper>
          </Fade>
        </Grid>
      </Grid>

      {/* Employee Utilization Chart */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Fade in={isLoaded} timeout={1100}>
            <Paper sx={{
              p: 4,
              height: 300,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <TrendingUp sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Typography variant="h6" sx={{ color: 'onSurface', fontWeight: 600 }}>
                  Employee Utilization Trends
                </Typography>
              </Box>
              <EmployeeUtilizationChart />
            </Paper>
          </Fade>
        </Grid>
      </Grid>

      {/* Last Update Info */}
      {lastUpdate && (
        <Fade in={isLoaded} timeout={1200}>
          <Box sx={{ mt: 4, textAlign: 'center', p: 2, borderRadius: 3, background: 'rgba(255, 255, 255, 0.5)', backdropFilter: 'blur(10px)' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Last updated: {lastUpdate.toLocaleString()}
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  )
}
